import { getApiUrl } from "@dashboard/common/utils";
import { camelizeKeys, decamelizeKeys } from "humps";
import {
  DeliveryDetails,
  DeliveryHistory,
  DeliveryProviderQuote,
  GeocodingResult,
  LogisticProviderConfig,
  OrderCalculation,
} from "../entities/delivery.entities";
import { normalizeProviderQuotes } from "../utils/delivery-create.utils";
import { normalizeDeliveryDetailRaw } from "../utils/delivery-detail.utils";
import { CREATE_ORDER_LOGISTIC_EVENT } from "../constants/logistic-states.constants";

const authHeaders = (token: string) =>
  ({
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  }) as { [key: string]: string };

const normalizeHistory = (history: DeliveryHistory): DeliveryHistory => ({
  all: history.all?.map(normalizeDeliveryDetailRaw),
  inProgress: history.inProgress?.map(normalizeDeliveryDetailRaw),
});

const normalizeProviderConfig = (raw: unknown): LogisticProviderConfig | null => {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  if (!item.id) return null;
  return {
    id: String(item.id),
    name: item.name ? String(item.name) : undefined,
    deliveryType: item.deliveryType
      ? String(item.deliveryType)
      : item.delivery_type
        ? String(item.delivery_type)
        : undefined,
    descriptionTime: item.descriptionTime
      ? String(item.descriptionTime)
      : undefined,
    enabled: item.enabled !== false,
  };
};

export const fetchLogisticProviders = async (
  token: string
): Promise<LogisticProviderConfig[]> => {
  const response = await fetch(`${getApiUrl()}/api/ms/logistic/providers`, {
    headers: authHeaders(token),
  });
  if (response.status !== 200) return [];
  const json = await response.json();
  const list = Array.isArray(json) ? json : [];
  return list
    .map((item) => normalizeProviderConfig(camelizeKeys(item)))
    .filter((p): p is LogisticProviderConfig => p !== null);
};

export const fetchDeliveryHistory = async (
  token: string
): Promise<DeliveryHistory> => {
  const response = await fetch(
    `${getApiUrl()}/api/ms/logistic/orders/home`,
    { headers: authHeaders(token) }
  );
  if (response.status !== 200) throw new Error("Failed to fetch delivery history");
  const json = await response.json();
  return normalizeHistory(camelizeKeys(json) as DeliveryHistory);
};

export const fetchDeliveryDetails = async (
  token: string,
  orderId: number
): Promise<DeliveryDetails> => {
  const response = await fetch(
    `${getApiUrl()}/api/ms/logistic/orders/${orderId}`,
    { headers: authHeaders(token) }
  );
  if (response.status !== 200) throw new Error("Failed to fetch delivery details");
  const json = await response.json();
  const data = Array.isArray(json) ? json[0] : json;
  return normalizeDeliveryDetailRaw(camelizeKeys(data) as DeliveryDetails);
};

export const fetchCalculateAllProviders = async (
  token: string,
  calculation: OrderCalculation
): Promise<DeliveryProviderQuote[]> => {
  const response = await fetch(
    `${getApiUrl()}/api/ms/logistic/calculate/all`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(decamelizeKeys(calculation)),
    }
  );
  if (response.status !== 200) throw new Error("Failed to calculate delivery");
  const json = await response.json();
  return normalizeProviderQuotes(camelizeKeys(json));
};

export const fetchCreateOrderLogistic = async (
  token: string,
  orderId: number,
  data?: Record<string, unknown>
): Promise<void> => {
  const response = await fetch(
    `${getApiUrl()}/api/ms/order/modification/${orderId}/modification`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({
        event: CREATE_ORDER_LOGISTIC_EVENT,
        event_id: String(orderId),
        data: data ? decamelizeKeys(data) : null,
      }),
    }
  );
  if (response.status !== 200) throw new Error("Failed to create order logistic");
};

export const fetchCreateDeliveryAsOrder = async (
  token: string,
  calculation: OrderCalculation
): Promise<{ id: number }> => {
  const logisticMap = decamelizeKeys(calculation) as Record<string, unknown>;
  const body = {
    delivery_type: "delivery_home",
    payment_method: calculation.paymentMethod || "online",
    origin_type: "logistic",
    total_value: calculation.totalValue,
    logistic_information: logisticMap,
  };
  const response = await fetch(`${getApiUrl()}/api/ms/order/owner`, {
    method: "POST",
    headers: { ...authHeaders(token), "X-API-VERSION": "3" },
    body: JSON.stringify(body),
  });
  if (response.status !== 200) throw new Error("Failed to create delivery order");
  const json = await response.json();
  return camelizeKeys(json) as { id: number };
};

export const fetchRecreateLogistic = async (
  token: string,
  orderId: number
): Promise<DeliveryDetails> => {
  const response = await fetch(
    `${getApiUrl()}/api/ms/logistic/recreate/${orderId}`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({}),
    }
  );
  if (response.status !== 200) throw new Error("Failed to recreate logistic");
  const json = await response.json();
  const data = Array.isArray(json) ? json[0] : json;
  return normalizeDeliveryDetailRaw(camelizeKeys(data) as DeliveryDetails);
};

export const fetchCancelLogistic = async (
  token: string,
  orderId: number
): Promise<void> => {
  const response = await fetch(`${getApiUrl()}/api/ms/logistic/cancel`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ order_id: orderId, description: "error" }),
  });
  if (response.status !== 200) throw new Error("Failed to cancel logistic");
};

export const fetchGeocoding = async (
  token: string,
  address: string
): Promise<GeocodingResult[]> => {
  const params = new URLSearchParams({
    types: "street_address",
    address,
  });
  const response = await fetch(
    `${getApiUrl()}/api/ms/location/geocoding?${params}`,
    { headers: authHeaders(token) }
  );
  if (response.status !== 200) throw new Error("Geocoding failed");
  const json = await response.json();
  return camelizeKeys(json) as GeocodingResult[];
};

export const payLogisticOrder = async (
  token: string,
  orderPaymentId: number
): Promise<void> => {
  const response = await fetch(
    `${getApiUrl()}/api/ms/payments/orders/pay/${orderPaymentId}`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({
        payment_method_option: {
          provider: "STRIPE",
          type: "ALL",
        },
        is_collect: true,
      }),
    }
  );
  if (response.status !== 200) {
    throw new Error("Failed to pay logistic order");
  }
};
